import { useEffect, useState } from 'react'
import { QueryObserverResult, useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import Cognito from '@core/cognito'
import Constants from '@core/utils/constants'
import { useMFAToken } from './useMFAToken'
import { useLastOrganisation } from './useLastOrganisation'
import { Profile } from '@core/entities/profile'
import { useGetProfile } from '@core/react-query/features/profile/hooks/useGetProfile'
import {
  isMFAExpiredTokenError,
  isMFARequiredError,
  isMFASessionNotFoundError,
  isMFASessionNotVerifiedError,
  isMFASkippedSessionExpiredError,
} from '@core/authentication'
import { PageUrls } from '@core/page-urls'
import { profileKeys } from '@core/react-query/features/profile/profileKeys'
import { RequestErrorType } from '@core/utils/api-axios-instance'
import cookies from 'js-cookie'

export type MFAStatus = 'none' | 'required' | 'expired' | 'verified'

export interface WebAuthState {
  isReady: boolean
  isAuthenticated: boolean
  mfaStatus: MFAStatus
  mfaToken?: string
  user?: Profile
}

export interface WebAuthActions {
  refreshAuthState: () => Promise<void>
  refetchUser: () => Promise<QueryObserverResult<Profile, RequestErrorType>>
  redirectOnLogin: (forceRedirect?: boolean) => void
  setMFARequired: () => void
  setMFAExpired: () => void
  setMFAToken: (token: string) => void
  clearMFAState: () => void
  getMFAToken: () => string | null
  clearMFAToken: () => void
  getMFAExpiry: () => string | null
  storeMFAExpiry: (expiresAt: string) => void
  getMFAEmail: () => string | null
  storeMFAEmail: (email: string) => void
  isSessionValid: () => boolean
  clearMFASession: () => void
  signout: () => void
}

export const useWebAuthentication = (): WebAuthState & WebAuthActions => {
  const router = useRouter()
  const queryClient = useQueryClient()
  const {
    getMFAToken,
    storeMFAToken,
    clearMFAToken,
    getMFAExpiry,
    storeMFAExpiry,
    getMFAEmail,
    storeMFAEmail,
    isSessionValid,
    clearMFASession,
  } = useMFAToken()
  const { getLastOrganisationId, clearLastOrganisationId } =
    useLastOrganisation()
  const { data: user, refetch: refetchProfile } = useGetProfile({
    enabled: false, // We'll fetch explicitly
  })

  const [state, setState] = useState<WebAuthState>({
    isReady: false,
    isAuthenticated: false,
    mfaStatus: 'none',
  })

  const refreshUserProfile = async () => {
    const cognitoUsername = Cognito.usernameSync()
    if (cognitoUsername) {
      await queryClient.fetchQuery({
        ...profileKeys.profile._ctx.byUserId({
          userId: cognitoUsername,
        }),
        staleTime: 0, // Force fresh fetch
      })
    }
  }

  const getRedirectPath = () => {
    if (typeof window !== 'undefined') {
      // Using window to get the pathname and search params
      // This is because there is a difference in the path encoding
      // Linux vs Ubuntu (CI)
      const fullPath = window.location.pathname + window.location.search

      // Testcafe seems to add junk into the path that the browser is aware of
      // window.location.pathname = /PQg4VDGGw*sPUiFMhaW/http://localhost:3000/actual/path
      // This removes the prefix junk so that we get just the path
      if (Constants.E2E && fullPath.includes('localhost')) {
        return fullPath.split('http://localhost:3000')?.[1] || fullPath
      }

      return fullPath
    }

    return router.asPath
  }

  const redirectOnRequiredAuthSession = () => {
    const redirectPath = getRedirectPath()
    const newPath = `/login?redirect=${encodeURIComponent(redirectPath)}`
    router.push(newPath)
  }

  const redirectOnMFARequiredSession = () => {
    const redirectPath = getRedirectPath()
    const mfaPath = `/mfa?redirect=${encodeURIComponent(redirectPath)}`

    router.replace(mfaPath, mfaPath, { shallow: true })
  }

  const redirectOnLogin = (forceRedirect?: boolean): void => {
    // Skip redirect for invite acceptance unless forced
    if (window.location.href.includes('accept-invite') && !forceRedirect) {
      return
    }

    if (!user) return

    const getRedirectDestination = (): string => {
      // Priority 1: Explicit redirect param
      if (router.query.redirect) {
        return router.query.redirect as string
      }

      // Priority 2: Super user → admin
      if (user.isSuperUser()) {
        return PageUrls.adminOrganisations
      }

      // Priority 3: Self-registered user with company onboarding → company onboarding page
      if (
        user.isSelfRegistered() &&
        user.companyOnboarding &&
        !user?.organisations?.[0]?.id
      ) {
        return PageUrls.companyOnboarding({
          organisationId: user.companyOnboarding.organisationId,
        })
      }

      // Priority 4: No organization → no-org page
      if (!user.organisations?.[0]?.id) {
        return PageUrls.noOrganisation
      }

      // Priority 5: Organisation this user last visited in this browser
      const lastOrganisationId = getLastOrganisationId(user.id)
      if (lastOrganisationId) {
        if (user.isMemberOfOrganisation(lastOrganisationId)) {
          return PageUrls.organisation(lastOrganisationId)
        }
        // Stale entry: the user is no longer a member of that organisation
        clearLastOrganisationId(user.id)
      }

      // Priority 6: First organization
      return PageUrls.organisation(user.organisations[0].id)
    }

    const destination = getRedirectDestination()
    router.replace(destination, destination, { shallow: true })
  }

  const verifyAuthSession = async (): Promise<boolean> => {
    try {
      // Don't run on server side
      if (typeof window === 'undefined') {
        return false
      }

      // Single-run guard - don't run if already ready (like useUserRedirect)
      if (state.isReady) {
        return true
      }

      // Check Cognito authentication first
      // @ts-ignore - accessing private member for synchronous check
      const cognitoUsername = Cognito.usernameSync()

      if (!cognitoUsername) {
        // Not authenticated with Cognito - redirect to login
        // Don't set isReady=true when redirecting (like useUserRedirect)
        setState({
          isReady: false, // ✅ Keep false when redirecting
          isAuthenticated: false,
          mfaStatus: 'none',
        })

        // Only redirect if we're not already on login/auth pages
        const currentPath = router.pathname
        const authPages = [
          // Email & Password Auth
          '/login',

          // SSO Auth
          '/oauth/web-redirect',
          '/oauth/mobile-redirect',

          // Logout
          '/oauth/web-logout',

          // Forgot Password
          '/forgot-password',
          '/forgot-password-email-sent',
          '/reset-password',
          '/reset-password-success',

          // MFA
          '/mfa',
          '/mfa/auto-mfa-verification',

          // Intent
          '/organisations/accept-invite',
          '/intent',

          // Company Self-Registration
          '/organisations/[id]/companies/onboarding',

          // External User Mobile Briefing
          '/mobile/briefing',

          //E2E TESTING
          '/testing/feature-flags',
          '/testing/toast',
        ]

        if (!authPages.includes(currentPath)) {
          redirectOnRequiredAuthSession()
        }

        return false
      }

      setState((prev) => ({
        ...prev,
        isReady: true,
        isAuthenticated: true,
        mfaStatus: 'none',
      }))

      // User is authenticated with Cognito
      return true
    } catch (error) {
      setState({
        isReady: true, // Set ready even on error to prevent infinite loops
        isAuthenticated: false,
        mfaStatus: 'none',
      })

      return false
    }
  }

  const verifyMFASession = async (): Promise<void> => {
    try {
      // Check for existing MFA token
      const existingMFAToken = getMFAToken()

      // Always try to fetch profile to determine MFA requirements
      // This will trigger interceptors if MFA is required but token is missing/expired
      await refreshUserProfile()

      // If we reach here, profile fetch succeeded
      setState({
        isReady: true,
        isAuthenticated: true,
        mfaStatus: existingMFAToken ? 'verified' : 'none',
      })
    } catch (error) {
      // Only redirect if we're not already on mfa/auth pages
      const currentPath = router.pathname
      const mfaPages = ['/mfa', '/mfa/auto-mfa-verification', '/intent']

      const errorData = error?.axiosError?.response?.data

      // Check if this is an MFA-required error
      if (
        isMFARequiredError(errorData?.errorType) ||
        isMFASessionNotFoundError(errorData?.errorType)
      ) {
        setMFARequired()

        if (!mfaPages.includes(currentPath)) {
          redirectOnMFARequiredSession()
        }
      }

      // Check if this is an MFA-expired error
      if (
        isMFAExpiredTokenError(errorData?.errorType) ||
        isMFASkippedSessionExpiredError(errorData?.errorType) ||
        isMFASessionNotVerifiedError(errorData?.errorType)
      ) {
        if (!mfaPages.includes(currentPath)) {
          setMFAExpired()
        }
      }
    }
  }

  const checkAuthAndMFA = async (): Promise<void> => {
    const isAuthenticated = await verifyAuthSession()

    if (isAuthenticated) {
      await verifyMFASession()
    }
  }

  // Check on mount and when router pathname changes (for login redirects)
  useEffect(() => {
    if (router.isReady) {
      checkAuthAndMFA()
    }
  }, [router.asPath, router.isReady]) // Re-check when pathname changes (like after OAuth redirect)

  // Actions
  const refreshAuthState = async (): Promise<void> => {
    // Reset ready state to allow re-checking
    setState((prev) => ({ ...prev, isReady: false }))
    await checkAuthAndMFA()
  }

  const setMFARequired = (): void => {
    setState((prev) => ({
      ...prev,
      mfaStatus: 'required',
    }))
  }

  const setMFAExpired = (): void => {
    setState((prev) => ({
      ...prev,
      mfaToken: undefined,
      mfaStatus: 'expired',
    }))
  }

  const setMFAToken = (token: string): void => {
    storeMFAToken(token)
    setState((prev) => ({
      ...prev,
      mfaToken: token,
    }))
  }

  const clearMFAState = (): void => {
    clearMFAToken()
    setState((prev) => ({
      ...prev,
      mfaStatus: 'none',
      mfaToken: undefined,
    }))
  }

  const signout = (): void => {
    Cognito.logout()
    clearMFAToken()
    cookies.remove('token')

    // @ts-ignore
    if (window.fcWidget) {
      // @ts-ignore
      window.fcWidget.destroy()
    }
  }

  return {
    ...state,
    user,
    refreshAuthState,
    refetchUser: refetchProfile,
    redirectOnLogin,
    setMFARequired,
    setMFAExpired,
    setMFAToken,
    clearMFAState,
    getMFAToken,
    clearMFAToken,
    getMFAExpiry,
    storeMFAExpiry,
    getMFAEmail,
    storeMFAEmail,
    isSessionValid,
    clearMFASession,
    signout,
  }
}
